'use strict';
//***********************************************************************************
//***********************************************************************************
import { cn_pastille } from './cn_pastille';
import { cn_add, cn_box, cn_cart, cn_clone, cn_dist, cn_dot, cn_middle, cn_mul, cn_normal, cn_normalize, cn_polar, cn_sub } from '../utils/cn_utilities';
import { cn_space } from '../model/cn_space';
import { cn_scene } from '../model/cn_scene';
import { cn_scene_controller } from './cn_scene_controller';
import { cn_roof_controller } from './cn_roof_controller';
import { cn_event_handler } from './cn_event_handler';
import { cn_mouse_event } from './cn_mouse_event';
import { cn_camera } from './cn_camera';
import { cn_number_input, cn_number_list_input } from './cn_inputs';
import { cn_snap } from './cn_snap';
import { cn_handler_resize } from './cn_handler_resize';
import { cn_handler_object } from './cn_handler_object';
import { cn_handler_rotate } from './cn_handler_rotate';
import { cn_handler_translate } from './cn_handler_translate';
import { cn_duplicator } from '../model/cn_duplicator';
import { cn_handler_wall_drag } from './cn_handler_wall_drag';
import { cn_handler_wall_distance } from './cn_handler_wall_distance';
import { cn_opening } from '../model/cn_opening';
import { cn_element } from '../model/cn_element';
import { cn_building } from '../model/cn_building';

/**
 * A handler to align several elements
 * @class cn_align_handler
 */
export class cn_align_handler extends cn_event_handler {
    //*****************************************************************
    /**
     * Constructor
     * @param {cn_element[]} elements
     * @param {cn_event_handler} parent
     */
    constructor(elements, parent) {
        super(parent);
        this.elements = elements;
        this.lock_index = 0;
        this.initial_positions = this.elements.map(e => e.get_align_center());

        //*** Initialize direction */
        this.direction = [0,0];
        let highest = 0;
        this.initial_positions.forEach(p => {
            let d = cn_dist(p,this.initial_positions[0]);
            if (d > highest) {
                this.direction = cn_sub(p,this.initial_positions[0]);
                highest = d;
            }
        });
        cn_normalize(this.direction);
        
        this._mouseover_lock = -1;
        this._space_segment = null;
        this._mouseover = -1;

        this._check_creation = new cn_pastille([0, 0], 'check_white.svg');
        this._check_creation.svg_class = 'pastille_check';
        this._check_creation.offset = [0,50];

        this._cancel_creation = new cn_pastille([0, 0], 'close_white.svg');
        this._cancel_creation.svg_class = 'pastille_undo';
        this._cancel_creation.offset = [0,100];

    }

    draw(camera) {
        let html = "";

        if (this._space_segment) {
            let s0 = camera.world_to_screen(this._space_segment.points[0]);
            let s1 = camera.world_to_screen(this._space_segment.points[1]);
            html += `<line x1="${s0[0]}" y1="${s0[1]}"  x2="${s1[0]}" y2="${s1[1]}" stroke="rgb(255, 0, 255)" stroke-width="3" />`;
        }

        let p0 = this.initial_positions[this.lock_index];
        let xmin = 0; let xmax = 0;
        this.initial_positions.forEach((p,i) => {
            let x = cn_dot(this.direction,cn_sub(p,p0));
            xmin = Math.min(x,xmin);
            xmax = Math.max(x,xmax);
            html += camera.draw_lock(p,i == this.lock_index,this._mouseover_lock == i);
            this.elements[i].set_align_center(cn_add(p0,cn_mul(this.direction,x)),p0);
            html += this.elements[i].draw(camera,[]);
            this.elements[i].set_align_center(p,p0);
        });
        this.vertices = [cn_add(p0,cn_mul(this.direction,xmin-1)),cn_add(p0,cn_mul(this.direction,xmax+1))]

        let v0 = camera.world_to_screen(this.vertices[0]);
        let v1 = camera.world_to_screen(this.vertices[1]);
        html += `<line x1="${v0[0]}" y1="${v0[1]}"  x2="${v1[0]}" y2="${v1[1]}" stroke="rgb(255,130,0)" stroke-width="${this._mouseover == 0 ? 7:3}" />`;

        this._check_creation.position = p0;
        this._cancel_creation.position = p0;
        html += this._check_creation.draw(camera);
        html += this._cancel_creation.draw(camera);
        return html;
    }

    clear_move() {
        this._mouseover_lock = -1;
        this._mouseover = -1;
        this._space_segment = null;
        this._check_creation.mouseover = false;
        this._cancel_creation.mouseover = false;
    }

    click(mouse_event) {
        if (this._mouseover_lock>=0) {
            this.lock_index = this._mouseover_lock;
            return true;
        }
        if (this._check_creation.mouseover) {
            let transaction_manager = this.elements[0].get_ascendant(cn_building).transaction_manager;
            transaction_manager.push_transaction("Alignement");
            let p0 = this.initial_positions[this.lock_index];
            this.initial_positions.forEach((p,i) => {
                let x = cn_dot(this.direction,cn_sub(p,p0));
                this.elements[i].set_align_center(cn_add(p0,cn_mul(this.direction,x)),p0,transaction_manager);
            });
            this.elements[0].get_ascendant(cn_scene).update_deep();
        }
        if (this._check_creation.mouseover || this._cancel_creation.mouseover) {
            // @ts-ignore
            this._parent.set_align_mode(false);
        }
        return true;
    }

    grab(mouse_event) {
        return this._mouseover == 0;
    }

    drag(mouse_event) {
        if (this._mouseover != 0) return false;
        this._space_segment = this.elements[0].get_ascendant(cn_scene).find_space_segment(mouse_event.mouse_world,mouse_event.camera.snap_world_distance);
        if (this._space_segment)
            this.direction = this._space_segment.normal;
        else
            this.direction = cn_sub(mouse_event.mouse_world,this.initial_positions[this.lock_index]);
        cn_normalize(this.direction);
        return true;
    }

    move(mouse_event) {

        this._check_creation.mouseover = this._check_creation.contains(mouse_event.mouse_world, mouse_event.camera);
        if (this._check_creation.mouseover)
            return true;
        
        this._cancel_creation.mouseover = this._cancel_creation.contains(mouse_event.mouse_world, mouse_event.camera);
        if (this._cancel_creation.mouseover)
            return true;
        
        this._mouseover_lock = this.initial_positions.findIndex(p => mouse_event.camera.on_vertex(mouse_event.mouse_world, p));
        if (this._mouseover_lock>=0) 
            return true;

        if (mouse_event.camera.on_edge(mouse_event.mouse_world, this.vertices[0],this.vertices[1])) {
            this._mouseover = 0;
            return true;
        }

        return true;
    }

}

